
import { mapActions, mapGetters, mapState } from "vuex";
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { EventBus } from "@/eventBus";
import Insight from "./Insight.vue";
import { RootGetters } from "@/store/getters";
import { TextArea } from "@progress/kendo-vue-inputs";
import SlidePreview from "@/modules/slides/components/SlidePreview.vue";
import { reportsActions, reportsGetters } from "@/store/reports/types";
import WorkflowStateButton from "@/components/header/WorkflowStateButton.vue";
import { debounce, inputElements } from "@/core/utils";
import { Button } from "@progress/kendo-vue-buttons";
import { Observation, SlideReport } from "@/store/reports/models";
import IButton from "@/components/buttons/IButton.vue";
// import useHotkey, {HotKey} from 'vue3-hotkey'
import { Direction } from "@/utils/navigation";
import { TechnicalScreeningClasses } from "@/models/slide";
import IbIcons from "@/components/icons/ib-icons.vue";

export default defineComponent({
  name: "Insights",
  data() {
    return {
      currentSlideReport: null,
      activeInsightKey: "",
      isFormValid: true,
      active: true,
    };
  },
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    // const active = ref(true);
    // const hotkeys = ref<HotKey[]>([
    //   {
    //     keys: ["s"],
    //     handler(keys) {
    //       active.value = !active.value;
    //     }
    //   }
    // ])
    // useHotkey(hotkeys.value)
    return { t };
  },
  components: {
    Insight,
    SlidePreview,
    KTextarea: TextArea,
    WorkflowStateButton,
    IButton,
    kbutton: Button,
    IbIcons,
  },
  computed: {
    ...mapGetters({
      config: RootGetters.CONFIG,
      activeSlide: RootGetters.ACTIVE_SLIDE,
      activeCase: RootGetters.GET_ACTIVE_CASE,
      slideInsights: RootGetters.GET_SLIDE_INSIGHTS,
      slides: RootGetters.SLIDES,
    }),
    ...mapGetters("reportsModule", {
      reportId: reportsGetters.ACTIVE_REPORT_ID,
      slideObservations: reportsGetters.SLIDE_OBSERVATIONS,
      slideReport: reportsGetters.SLIDE_REPORT,
      observation: reportsGetters.OBSERVATION,
    }),
    ...mapState(["activeCaseId", "activeSlideId"]),
    isSlideAnalyzed() {
      return !TechnicalScreeningClasses.includes(this.activeSlide.screeningClass);
    },
    getPrefill(): Function {
      return (key: string, controller: any) => {
        const observation: any = this.currentSlideReport?.observations.find(
          (observation: Observation) => {
            return observation.key === key;
          }
        );
        if (controller.type === "input") {
          switch (controller.inputType) {
            case "number":
              return observation?.value >= 0 ? observation.value : null;
            case "text":
            default:
              return observation?.value || "";
          }
        }
        return observation?.value || "";
      };
    },
  },
  methods: {
    ...mapActions("reportsModule", {
      setSlideObservations: reportsActions.SET_SLIDE_OBSERVATIONS,
      updateReport: reportsActions.UPDATE_REPORT,
      loadOrCreateReport: reportsActions.LOAD_OR_CREATE_REPORT,
    }),
    toggleInsights() {
      this.active = !this.active;
    },
    handleChange(newObservation: Observation) {
      this.isFormValid = this.$refs?.obsForm?.reportValidity();
      if (!this.isFormValid) {
        return;
      }
      if (this.currentSlideReport[newObservation.key]) {
        this.currentSlideReport[newObservation.key].value = newObservation.value;
      } else {
        let currentObservationObj = this.currentSlideReport.observations.find(
          (observation: Observation) => {
            return newObservation.key === observation.key;
          }
        );
        if (currentObservationObj) {
          currentObservationObj.title = newObservation.title;
          currentObservationObj.value = newObservation.value;
        } else {
          this.currentSlideReport.observations.push(newObservation);
        }
      }
      debounce(() => {
        this.saveSlideObservations(this.currentSlideReport);
      }, 1000);
    },
    saveSlideObservations(slideReport: SlideReport) {
      this.setSlideObservations({
        caseId: this.activeCase.caseId,
        slideId: this.activeSlide.slideId,
        slideReport,
      });
      this.updateReport({
        caseId: this.activeCaseId,
        reportId: this.reportId,
      });
    },
    initSlideObservations() {
      /* To whomever is going to maintain this function in the future - I'M SORRY!
       * Some explanation
       * ================
       * Given 2 data objects: Observations and Insights.
       * This method should map between the Insights keys, and the Observations data,
       * and merge that into currentSlideReport.casesReports array
       * Insights object will look something like:
       * [
       *   { key: "cancer", ... },
       *   { key: "pni", ... }, ...
       * ]
       * Observations object will look something like:
       * {
       *   casesReports: [
       *     { key: "cancer", "title": "Cancer", value: "lorem ipsum" },
       *     { key: "pni", "title": "Perineural invasion", value: "dolor sit emet" }, ...
       *   ],
       *   isComplete: false,
       *   comments: {
       *     title: "Comments",
       *     value: ""
       *   }
       * }
       *
       * The casesReports array will map Insights into casesReports.
       * The rest is casesReports meta data
       */
      const vm = this;
      const slideObservationsMeta = this.slideReport(this.activeCaseId, this.activeSlideId);
      this.currentSlideReport = { ...this.createEmptySlideReport(), ...slideObservationsMeta };
      this.currentSlideReport.observations = this.slideInsights.map(
        (insight: { key: string; title: string }) => {
          const observation = vm
            .slideObservations(vm.activeCaseId, vm.activeSlideId)
            .find((observation) => {
              return observation.key === insight.key;
            });
          return {
            title: observation?.title,
            key: insight.key,
            value: observation?.value,
          };
        }
      );
    },
    createEmptySlideReport(): SlideReport {
      return {
        observations: [],
        comments: {
          value: "",
          title: "Comments",
        },
        isComplete: false,
      };
    },
    setActiveInsight(insight: any) {
      if (this.activeInsightKey === insight.key) {
        return;
      }
      this.activeInsightKey = insight.key;
      setTimeout(() => {
        EventBus.emit("active-insight-changed", {
          insight,
          insightObservation: this.observation(this.activeCaseId, this.activeSlideId, insight.key),
          isReportComplete: this.currentSlideReport.isComplete,
        });
        if (this.config.enable_zoom_out_on_insight_change) {
          EventBus.emit("event-bridge-api", { eventName: "active-insight-changed" });
        }
      }, 0);
    },
    handleWorkflowStateChange(payload: { isComplete: boolean }) {
      this.currentSlideReport.isComplete = payload.isComplete;
      this.saveSlideObservations(this.currentSlideReport);
      if (payload.isComplete) {
        // automatically navigating to the next incomplete slide:
        EventBus.emit("navigateToSlideEvent", {
          navigationDirection: Direction.Next,
          isAutoSkipMode: true,
        });
      }
    },
    insightsKeyListener(e) {
      if (inputElements.includes(e.target.tagName.toLowerCase())) {
        return;
      }
      switch (e.key) {
        case "s":
        case "S":
          this.active = !this.active;
      }
    },
    heatmapSelected(heatmap: { classification: string }) {
      // find current active insight
      const activeInsight = this.activeSlide.insights.find((insight: { key: string }) => {
        return insight.key === this.activeInsightKey;
      });
      if (activeInsight && heatmap.classification !== activeInsight.associated_heatmap) {
        this.activeInsightKey = "";
      }
    },
    preventScrolling(event: KeyboardEvent) {
      if (event.key === "PageDown" || event.key === "PageUp" || event.key === " ") {
        event.preventDefault();
      }
    },
  },
  watch: {
    activeSlide(newVal, oldVal) {
      if (newVal) {
        if (newVal.slideId !== oldVal?.slideId) {
          this.activeInsightKey = "";
        }
        this.initSlideObservations();
        if (newVal?.insights?.length) {
          if (!this.activeInsightKey) {
            this.setActiveInsight(newVal.insights[0]);
          }
        }
      }
    },
    activeCaseId(newVal: string) {
      if (newVal) {
        this.loadOrCreateReport({ caseId: newVal });
      }
    },
  },
  mounted() {
    EventBus.on("heatmapSelected", this.heatmapSelected);
    window.addEventListener("keydown", this.insightsKeyListener, false);
  },
  unmounted() {
    EventBus.off("heatmapSelected", this.heatmapSelected);
    window.removeEventListener("keydown", this.insightsKeyListener, false);
  },
});
